import { getDashboardInternationalMarkets } from '@simplywallst/services';
import { withAuth } from '@utilities/queries';
import { toPercentage } from '@/utilities/formatting';
import { PRIMARY_MARKETS } from '@/constants/markets';
import { getPath } from '@/router/utils';
import { ROUTE_STOCKS_MARKET } from '@/constants/routes';

type ResponseCountry = Awaited<
  ReturnType<typeof getDashboardInternationalMarkets>
>['data']['data']['top']['return_7d'][number];

const formatterPercentage = (value: number | null) =>
  value !== null ? toPercentage(value, 1) : 'n/a';

export interface Country {
  countryName: string;
  countryISO: string;
  countryId: string;
  canonicalUrl: string;
  return7D: number | null;
  return7DFormatted: string;
  return30D: number | null;
  return30DFormatted: string;
  return90D: number | null;
  return90DFormatted: string;
  return1YrAbs: number | null;
  return1YrAbsFormatted: string;
  return3YrAbs: number | null;
  return3YrAbsFormatted: string;
  return5YrAbs: number | null;
  return5YrAbsFormatted: string;
}

export interface Periods {
  return7D: Country[];
  return30D: Country[];
  return90D: Country[];
  return1YrAbs: Country[];
  return3YrAbs: Country[];
  return5YrAbs: Country[];
}

export interface Response {
  keys: {
    top: {
      return7D: string[];
      return30D: string[];
      return90D: string[];
      return1YrAbs: string[];
      return3YrAbs: string[];
      return5YrAbs: string[];
    };
    worst: {
      return7D: string[];
      return30D: string[];
      return90D: string[];
      return1YrAbs: string[];
      return3YrAbs: string[];
      return5YrAbs: string[];
    };
  };
  entities: Record<string, Country>;
}

export const getDefault = (): Response => ({
  keys: {
    top: {
      return7D: [],
      return30D: [],
      return90D: [],
      return1YrAbs: [],
      return3YrAbs: [],
      return5YrAbs: [],
    },
    worst: {
      return7D: [],
      return30D: [],
      return90D: [],
      return1YrAbs: [],
      return3YrAbs: [],
      return5YrAbs: [],
    },
  },
  entities: {},
});

const getId = (entity: ResponseCountry): string =>
  JSON.stringify([entity.country, entity.country_id, entity.country_iso]);

export default async function queryFn(): Promise<Response | undefined> {
  const data = await withAuth(getDashboardInternationalMarkets);
  if (typeof data !== 'undefined') {
    const {
      data: {
        data: { top, worst },
      },
    } = data;

    const entities: Record<string, Country> = {};

    const getKeys = (arr: ResponseCountry[]) => {
      return arr.reduce<string[]>((a, b) => {
        const id = getId(b);
        if (typeof entities[id] === 'undefined') {
          const countryISO = (b.country_iso || '').toLowerCase();
          if (countryISO === 'kz') {
            /**
             * Bail out if kz
             * https://simplywallst.atlassian.net/wiki/spaces/OPS/pages/1152680032/Stop+supporting+Kazakhstan+Stock+Exchange+KAS
             * */
            return a;
          }
          entities[id] = {
            countryName: PRIMARY_MARKETS[countryISO].label,
            countryISO: countryISO,
            countryId: b.country_id.toString(),
            canonicalUrl: getPath({
              type: ROUTE_STOCKS_MARKET,
              payload: { market: countryISO },
            }),
            return7D: b.return_7d,
            return7DFormatted: formatterPercentage(b.return_7d),
            return30D: b.return_30d,
            return30DFormatted: formatterPercentage(b.return_30d),
            return90D: b.return_90d,
            return90DFormatted: formatterPercentage(b.return_90d),
            return1YrAbs: b.return_1yr_abs,
            return1YrAbsFormatted: formatterPercentage(b.return_1yr_abs),
            return3YrAbs: b.return_3yr_abs,
            return3YrAbsFormatted: formatterPercentage(b.return_3yr_abs),
            return5YrAbs: b.return_5yr_abs,
            return5YrAbsFormatted: formatterPercentage(b.return_5yr_abs),
          };
        }
        a.push(id);
        return a;
      }, []);
    };

    const keys = {
      top: {
        return7D: getKeys(top.return_7d),
        return30D: getKeys(top.return_30d),
        return90D: getKeys(top.return_90d),
        return1YrAbs: getKeys(top.return_1yr_abs),
        return3YrAbs: getKeys(top.return_3yr_abs),
        return5YrAbs: getKeys(top.return_5yr_abs),
      },
      worst: {
        return7D: getKeys(worst.return_7d),
        return30D: getKeys(worst.return_30d),
        return90D: getKeys(worst.return_90d),
        return1YrAbs: getKeys(worst.return_1yr_abs),
        return3YrAbs: getKeys(worst.return_3yr_abs),
        return5YrAbs: getKeys(worst.return_5yr_abs),
      },
    };

    return {
      entities,
      keys,
    };
  }
}
